export const SUPPORTED_LOCALES = ['en', 'pt-BR'] as const

export type Locale = (typeof SUPPORTED_LOCALES)[number]

export interface Translations {
  // App shell
  appName: string
  loading: string
  loadingDesc: string

  // Language switcher
  language: string
  settings: string
  usernameLabel: string
  guestUser: string
  themeMode: string
  darkMode: string
  lightMode: string
  quickLinks: string
  quickLinkHome: string
  quickLinkInvestments: string
  quickLinkAlbumSelection: string
  quickLinkThemeReview: string
  quickLinkHelp: string
  albumPageSize: string
  albumPageSizeValue: (size: number) => string
  helpTitle: string
  helpDescription: string

  // Album selector
  yourAlbums: string
  selectAlbumDesc: string
  myInvestmentAlbum: string
  newAlbum: string
  maxAlbumsReached: string
  openAlbum: string
  deleteAlbum: string

  // Create album modal
  newAlbumTitle: string
  configureCollection: string
  albumName: string
  albumNamePlaceholder: string
  albumSize: string
  stickersLabel: string
  theme: string
  availableThemes: (count: number) => string
  createAlbum: string
  cancel: string

  // Delete confirm modal
  confirmDeletion: string
  deleteThisAlbum: string
  deleteWarning: string
  deletePermanently: string

  // Album header
  albumSizeLabel: string
  themeLabel: string
  platformLabel: string
  allAlbums: string

  // Tabs
  tabAlbum: string
  tabFlipbook: string
  tabHistory: string
  tabConfiguration: string

  // Metrics
  collectedStickers: string
  investedAmount: string
  targetFinishDate: string
  targetNotDefined: string
  daysOverdue: (days: number) => string
  daysRemaining: (days: number) => string
  targetToday: string
  thisMonth: string
  activeMonth: string
  noActivityYet: string
  investmentsThisMonth: (count: number) => string
  noInvestments: string
  hideTarget: string
  showTarget: string
  ofTarget: string

  // Album / sticker grid
  stickerAlbum: string
  collectByInvesting: string
  clickToRegister: string
  collected: string
  openSlot: string

  // Flipbook
  flipBook: string
  browsePageByPage: string
  clickToInvest: string
  previousPage: string
  nextPage: string
  pageOf: (current: number, total: number) => string
  stickersRange: (from: number, to: number) => string
  jumpToPage: string

  // History
  investmentHistory: string
  allRecordedInvestments: string
  exportCSV: string
  noInvestmentsYet: string
  noInvestmentsDesc: string
  investmentsCount: (count: number) => string
  total: string
  stickerCol: string
  nameCol: string
  valueCol: string
  dateCol: string
  destinationCol: string
  commentsCol: string

  // Configuration
  configurationTitle: string
  albumSettings: string
  settingsEditable: string
  targetAmount: string
  revealCalculation: string
  hideTotalTarget: string
  showTotalTarget: string
  sequenceFormula: string
  investedSoFar: string
  targetHidden: string
  savedInvestments: string
  preconfigureDestinations: string
  noNotes: string
  remove: string
  destinationName: string
  typeLabel: string
  notesLabel: string
  addSavedInvestment: string
  currency: string
  targetFinishDateLabel: string

  // Default investment destination
  noDeclaration: string
  noDeclarationKind: string
  noDeclarationNotes: string

  // Collected sticker animation
  stickerCollectedTitle: string
  stickerCollectedClose: string

  // Sticker display
  rarityLabel: string

  // Sticker modal
  stickerInvestment: string
  resetSticker: string
  investIn: string
  comments: string
  commentsPlaceholder: string
  invest: string
  updateInvestment: string
  noDestinationsYet: string
  noDestinationsDesc: string
  goToConfiguration: string

  // Save error
  saveError: string
  saveRetry: string

  // Auth (web)
  authSubtitle: string
  authEmail: string
  authPassword: string
  authSignIn: string
  authSignUp: string
  authMagicLink: string
  authSendMagicLink: string
  authMagicLinkSent: string
  authConfirmEmail: string
  authSignOut: string
  authForgotPassword: string
  authSendPasswordReset: string
  authPasswordResetSent: string
  authUpdatePassword: string
  authConfirmPassword: string
  authPasswordMismatch: string
  authPasswordUpdated: string
  checkYourAlbums: string

  // Auth screen marketing copy
  marketingAriaLabel: string
  marketingKicker: string
  marketingTransformTitle: string
  marketingTransformLead: string
  marketingTransformP1: string
  marketingTransformP2: string
  marketingTransformP3: string
  marketingHowTitle: string
  marketingHowIntro: string
  marketingStep1Title: string
  marketingStep1Body: string
  marketingStep2Title: string
  marketingStep2Body: string
  marketingStep3Title: string
  marketingStep3Body: string
  marketingStep4Title: string
  marketingStep4Body: string
  marketingGaussTitle: string
  marketingGaussBody: string
  marketingFormula: string
  marketingHighlightsTitle: string
  marketingHighlight1: string
  marketingHighlight2: string
  marketingHighlight3: string
  marketingFooterLead: string
  marketingFooterEmphasis: string
  marketingFooterTagline: string

  // Public pages (auth screen)
  publicPagesLabel: string
  publicHome: string
  publicHowTo: string
  publicAbout: string
  publicContact: string
  publicTerms: string
  legalTermsTitle: string
  legalLoadError: string
  howToKicker: string
  howToTitle: string
  howToLead: string
  howToFreeRegistrationTitle: string
  howToFreeRegistrationBody: string
  howToFlowTitle: string
  howToFlow1Title: string
  howToFlow1Body: string
  howToFlow2Title: string
  howToFlow2Body: string
  howToFlow3Title: string
  howToFlow3Body: string
  howToFlow4Title: string
  howToFlow4Body: string
  howToReminderTitle: string
  howToReminderBody: string
  aboutKicker: string
  aboutTitle: string
  aboutLead: string
  aboutMissionTitle: string
  aboutMissionP1: string
  aboutMissionP2: string
  aboutNoIntermediationTitle: string
  aboutNoIntermediationP1: string
  aboutNoIntermediationP2: string
  aboutDisclaimer: string
  contactKicker: string
  contactTitle: string
  contactLead: string
  contactPrimaryChannelTitle: string
  contactPrimaryChannelBody: string
  contactPrimaryCta: string
  contactWhatToSendTitle: string
  contactItem1: string
  contactItem2: string
  contactItem3: string

  // Theme names
  themeNames: Record<string, string>
  themeSoccer: string
  themeMtg: string
  themeTourist: string
  themeRockroll: string

  // Theme hero texts
  heroSoccer: string
  heroMtg: string
  heroTourist: string
  heroRockroll: string
}

const ACTIVE_TRANSLATIONS: Record<Locale, Translations> = {
  en: {
    appName: 'InvestAlbum',
    loading: 'Loading your albums',
    loadingDesc: 'Restoring your album data from SQLite.',
    language: 'Language',
    settings: 'Settings',
    usernameLabel: 'Username',
    guestUser: 'Local user',
    themeMode: 'Theme',
    darkMode: 'Switch to dark mode',
    lightMode: 'Switch to light mode',
    quickLinks: 'Quick links',
    quickLinkHome: 'Home',
    quickLinkInvestments: 'Investments',
    quickLinkAlbumSelection: 'Album selection',
    quickLinkThemeReview: 'Theme review',
    quickLinkHelp: 'Help',
    albumPageSize: 'Album page size',
    albumPageSizeValue: (size) => `${size} stickers per page`,
    helpTitle: 'Help',
    helpDescription:
      'Use the album tabs to collect stickers, review investment history, and keep destinations updated.',

    yourAlbums: 'Your Albums',
    selectAlbumDesc: 'Select an album to open it, or create a new one.',
    myInvestmentAlbum: 'My Investment Album',
    newAlbum: 'New album',
    maxAlbumsReached: 'Maximum of 50 albums reached.',
    openAlbum: 'Open album',
    deleteAlbum: 'Delete',

    newAlbumTitle: 'New album',
    configureCollection: 'Configure your collection',
    albumName: 'Album name',
    albumNamePlaceholder: 'My Investment Journey',
    albumSize: 'Album size',
    stickersLabel: 'stickers',
    theme: 'Theme',
    availableThemes: (count) => `${count} standard album sizes available`,
    createAlbum: 'Create album',
    cancel: 'Cancel',

    confirmDeletion: 'Confirm deletion',
    deleteThisAlbum: 'Delete this album?',
    deleteWarning:
      'All sticker progress and investment records for this album will be permanently deleted. This cannot be undone.',
    deletePermanently: 'Delete permanently',

    albumSizeLabel: 'Album size',
    themeLabel: 'Theme',
    platformLabel: 'Platform',
    allAlbums: 'Album selection',

    tabAlbum: 'Album',
    tabFlipbook: 'Flip Book',
    tabHistory: 'History',
    tabConfiguration: 'Configuration',

    collectedStickers: 'Collected stickers',
    investedAmount: 'Invested amount',
    targetFinishDate: 'Target finish date',
    targetNotDefined: 'Not defined yet',
    daysOverdue: (d) => `${d} day${d !== 1 ? 's' : ''} overdue`,
    daysRemaining: (d) => `${d} day${d !== 1 ? 's' : ''} remaining`,
    targetToday: 'Target is today!',
    thisMonth: 'This month',
    activeMonth: 'Active month',
    noActivityYet: 'No activity yet',
    investmentsThisMonth: (c) => `${c} investment${c !== 1 ? 's' : ''}`,
    noInvestments: 'No investments',
    hideTarget: 'Hide target',
    showTarget: 'Show target',
    ofTarget: 'of target',

    stickerAlbum: 'Sticker album',
    collectByInvesting: 'Collect by investing in the selected destination',
    clickToRegister: 'Click any sticker to register the matching investment amount.',
    collected: 'Collected',
    openSlot: 'Open slot',

    flipBook: 'Flip Book',
    browsePageByPage: 'Browse your sticker collection page by page',
    clickToInvest: 'Click any sticker to invest or view details.',
    previousPage: 'Previous page',
    nextPage: 'Next page',
    pageOf: (c, t) => `Page ${c} / ${t}`,
    stickersRange: (f, t) => `stickers ${f}–${t}`,
    jumpToPage: 'Jump to page',

    investmentHistory: 'Investment history',
    allRecordedInvestments: 'All recorded investments for this album',
    exportCSV: 'Export CSV',
    noInvestmentsYet: 'No investments recorded yet.',
    noInvestmentsDesc: 'Collect your first sticker from the Album tab.',
    investmentsCount: (c) => `${c} investment${c !== 1 ? 's' : ''}`,
    total: 'Total',
    stickerCol: '#',
    nameCol: 'Name',
    valueCol: 'Value',
    dateCol: 'Date',
    destinationCol: 'Destination',
    commentsCol: 'Comments',

    configurationTitle: 'Configuration',
    albumSettings: 'Album settings',
    settingsEditable: 'Album settings remain editable after progress starts',
    targetAmount: 'Target amount',
    revealCalculation: 'Reveal the full album calculation',
    hideTotalTarget: 'Hide total target',
    showTotalTarget: 'Show total target',
    sequenceFormula: 'Sequence formula',
    investedSoFar: 'Invested so far',
    targetHidden: 'The total target stays hidden from the album page and appears only here.',
    savedInvestments: 'Saved investments',
    preconfigureDestinations: 'Preconfigure destinations used by the sticker flow',
    noNotes: 'No notes provided.',
    remove: 'Remove',
    destinationName: 'Destination name',
    typeLabel: 'Type',
    notesLabel: 'Notes',
    addSavedInvestment: 'Add saved investment',
    currency: 'Currency',
    targetFinishDateLabel: 'Target finish date',

    noDeclaration: "I don't want to declare",
    noDeclarationKind: 'Private',
    noDeclarationNotes: 'Investment details kept private.',

    stickerCollectedTitle: 'Sticker collected!',
    stickerCollectedClose: 'Close',

    rarityLabel: 'Rarity',

    stickerInvestment: 'Sticker investment',
    resetSticker: 'Reset sticker',
    investIn: 'Invest in',
    comments: 'Comments',
    commentsPlaceholder: 'Why this investment? What did you learn?',
    invest: 'Invest',
    updateInvestment: 'Update investment',
    noDestinationsYet: 'No saved investments configured yet.',
    noDestinationsDesc: 'Add at least one destination before collecting stickers.',
    goToConfiguration: 'Go to Configuration',

    saveError: 'Failed to save. Your progress may not be persisted.',
    saveRetry: 'Retry',

    authSubtitle: 'Sign in to sync your albums across devices.',
    authEmail: 'Email',
    authPassword: 'Password',
    authSignIn: 'Sign in',
    authSignUp: 'Create account',
    authMagicLink: 'Email me a link',
    authSendMagicLink: 'Send magic link',
    authMagicLinkSent: 'Check your email for a sign-in link.',
    authConfirmEmail: 'Check your email to confirm your account.',
    authSignOut: 'Sign out',
    authForgotPassword: 'Forgot password?',
    authSendPasswordReset: 'Send reset email',
    authPasswordResetSent: 'Check your email for a password reset link.',
    authUpdatePassword: 'Update password',
    authConfirmPassword: 'Confirm password',
    authPasswordMismatch: 'Passwords do not match.',
    authPasswordUpdated: 'Password updated. You can now sign in.',
    checkYourAlbums: 'Check your Albums',

    marketingAriaLabel: 'How InvestAlbum works',
    marketingKicker: 'Build your wealth one sticker at a time',
    marketingTransformTitle: 'Turn your investments into a collection',
    marketingTransformLead:
      'Saving money is not always motivating. Numbers grow slowly and progress can feel abstract.',
    marketingTransformP1:
      'With InvestAlbum, investing works like completing a sticker album: each album is a financial goal, and every sticker represents one investment amount.',
    marketingTransformP2:
      'You can collect stickers in any order. There is no perfect path, only consistency. Every sticker you place brings your target closer and makes progress visible.',
    marketingTransformP3:
      'When you collect the final sticker, the total of all recorded investments exactly matches the planned goal value for that album.',
    marketingHowTitle: 'How it works',
    marketingHowIntro: 'Simple, visual, and built for real investing habits.',
    marketingStep1Title: '1. Choose an album',
    marketingStep1Body:
      'Each album has a fixed number of stickers, such as 30, 50, or 100. The album size defines your progression scale.',
    marketingStep2Title: '2. Understand sticker values',
    marketingStep2Body:
      'Sticker values follow a growing sequence from 1 up to the album size. Lower numbers represent smaller contributions, higher numbers represent larger ones.',
    marketingStep3Title: '3. Invest at your own pace',
    marketingStep3Body:
      'Whenever you invest, choose the sticker that matches that amount and collect it. You can jump from sticker 50 to 3 to 28, in any order you prefer.',
    marketingStep4Title: '4. Complete the collection',
    marketingStep4Body:
      'After all stickers are collected, your total invested amount reaches the goal exactly. InvestAlbum uses the arithmetic series sum to define values and guarantee this result.',
    marketingGaussTitle: 'The story behind the formula',
    marketingGaussBody:
      'This idea comes from the classic Gauss story: by pairing values from the ends of the sequence, the total can be calculated quickly and precisely.',
    marketingFormula: 'Sum = n * (n + 1) / 2',
    marketingHighlightsTitle: 'Why this helps',
    marketingHighlight1:
      'Visual momentum: progress feels concrete because each action fills a real slot in your album.',
    marketingHighlight2:
      'Flexible planning: use saved destinations, comments, and history without losing your own investing rhythm.',
    marketingHighlight3:
      'Safe continuity: sync on the web when configured, plus JSON export/import for backup and migration.',
    marketingFooterLead: 'Invest. Learn. Collect.',
    marketingFooterEmphasis: 'Conquer.',
    marketingFooterTagline: 'Your future deserves to be incredible.',

    publicPagesLabel: 'Public pages',
    publicHome: 'Home',
    publicHowTo: 'How to use',
    publicAbout: 'About us',
    publicContact: 'Contact',
    publicTerms: 'Terms and conditions',
    legalTermsTitle: 'Terms and conditions',
    legalLoadError: 'Unable to load the legal text right now. Please try again in a moment.',
    howToKicker: 'Getting started',
    howToTitle: 'How to use InvestAlbum',
    howToLead:
      'InvestAlbum is a free registration website to organize your investment journey with visual milestones.',
    howToFreeRegistrationTitle: 'Free registration',
    howToFreeRegistrationBody:
      'Creating an account is free. You can sign in to sync your albums across devices and keep your progress backed up.',
    howToFlowTitle: 'Simple flow',
    howToFlow1Title: '1. Create your album',
    howToFlow1Body:
      'Pick a theme you enjoy. The selected theme defines album size and the sticker journey you will complete.',
    howToFlow2Title: '2. Choose a sticker when you invest',
    howToFlow2Body:
      'Each sticker has a number and a matching investment value. You can collect stickers in any order.',
    howToFlow3Title: '3. Link to your investment destination',
    howToFlow3Body:
      'Record where you invested, such as treasury bonds, ETF, or CDB, to keep a clean personal history.',
    howToFlow4Title: '4. Track progress until completion',
    howToFlow4Body:
      'As stickers are collected, your invested total moves toward the album target based on the arithmetic sequence sum.',
    howToReminderTitle: 'Important reminder',
    howToReminderBody:
      'InvestAlbum is for educational tracking only. It does not execute, broker, or intermediate financial transactions.',
    aboutKicker: 'About InvestAlbum',
    aboutTitle: 'We help people build investing consistency',
    aboutLead:
      'InvestAlbum exists to make long-term investing more engaging through visual progression and simple habits.',
    aboutMissionTitle: 'Our mission',
    aboutMissionP1:
      'We transform abstract financial goals into collectible journeys that are easier to follow and celebrate.',
    aboutMissionP2:
      'The platform is educational and organizational: each sticker is a milestone, each album is a goal.',
    aboutNoIntermediationTitle: 'No financial intermediation',
    aboutNoIntermediationP1:
      'InvestAlbum does not receive, hold, move, or invest your money. You invest directly through your own financial institution and then record your progress here.',
    aboutNoIntermediationP2:
      'There are no in-app trades, transfers, redemptions, or portfolio management actions.',
    aboutDisclaimer:
      'InvestAlbum is an educational and tracking tool. It does not provide financial products, brokerage services, or investment advice.',
    contactKicker: 'Talk to us',
    contactTitle: 'Contact page',
    contactLead:
      'Questions, bug reports, and suggestions are welcome. We use your feedback to improve usability and reliability.',
    contactPrimaryChannelTitle: 'Main channel',
    contactPrimaryChannelBody:
      'Open an issue in the official repository and include context so we can investigate quickly.',
    contactPrimaryCta: 'Open contact/issues page',
    contactWhatToSendTitle: 'What helps us respond faster',
    contactItem1: 'A short description of what happened and what you expected.',
    contactItem2: 'Device/browser information and your selected language.',
    contactItem3: 'Steps to reproduce and screenshots when possible.',

    themeNames: {
      standard: 'Standard Album',
      animals: '500 Animals',
      naturalWonders: '500 Natural Wonders',
      architecture: "Architect's Blueprint",
      artists: 'Artists',
      backpacker: 'Backpacker Atlas',
      botanical: 'Botanical',
      castle: 'Castle and Fortress',
      compounds: 'Compounds',
      cosmos: 'Cosmos',
      inventions: 'Inventions',
      marine: 'Marine Life',
      mustVisitCities: '500 Must-Visit Cities in the World',
      mtgCommander: 'MTG Commander Staples',
      mtgRevised: 'MTG Revised Edition',
      pokemon: 'Pokemon',
      pokemonOriginal151: 'Pokemon Original 151',
      rockroll: 'Rock Anthems',
      soccer: 'Soccer',
      tourist: 'Wonders of the World',
      turningpoints: 'Turning Points',
      unesco: 'UNESCO'
    },
    themeSoccer: '500 Soccer Legends',
    themeMtg: '500 MTG Legends',
    themeTourist: '500 Wonders of the World',
    themeRockroll: '500 Rock Anthems',

    heroSoccer: 'The greatest footballers of all time — from Pelé to the present day.',
    heroMtg: 'The most powerful and iconic Magic: The Gathering cards ever printed.',
    heroTourist: "From the Eiffel Tower to Machu Picchu — the world's greatest destinations.",
    heroRockroll: 'The songs and artists that defined rock and roll history.'
  },

  'pt-BR': {
    appName: 'InvestAlbum',
    loading: 'Carregando seus albums',
    loadingDesc: 'Restaurando seus dados do SQLite.',
    language: 'Idioma',
    settings: 'Configurações',
    usernameLabel: 'Usuário',
    guestUser: 'Usuário local',
    themeMode: 'Tema',
    darkMode: 'Ativar modo escuro',
    lightMode: 'Ativar modo claro',
    quickLinks: 'Links rápidos',
    quickLinkHome: 'Início',
    quickLinkInvestments: 'Investimentos',
    quickLinkAlbumSelection: 'Seleção de albums',
    quickLinkThemeReview: 'Revisão dos temas',
    quickLinkHelp: 'Ajuda',
    albumPageSize: 'Tamanho da página do album',
    albumPageSizeValue: (size) => `${size} figurinhas por página`,
    helpTitle: 'Ajuda',
    helpDescription:
      'Use as abas do album para coletar figurinhas, revisar investimentos e manter destinos atualizados.',

    yourAlbums: 'Seus Albums',
    selectAlbumDesc: 'Selecione um album para abri-lo ou crie um novo.',
    myInvestmentAlbum: 'Meu Album de Investimentos',
    newAlbum: 'Novo album',
    maxAlbumsReached: 'Limite de 50 albums atingido.',
    openAlbum: 'Abrir album',
    deleteAlbum: 'Excluir',

    newAlbumTitle: 'Novo album',
    configureCollection: 'Configure sua coleção',
    albumName: 'Nome do album',
    albumNamePlaceholder: 'Minha Jornada de Investimentos',
    albumSize: 'Tamanho do album',
    stickersLabel: 'figurinhas',
    theme: 'Tema',
    availableThemes: (count) => `${count} tamanhos de álbum padrão disponíveis`,
    createAlbum: 'Criar album',
    cancel: 'Cancelar',

    confirmDeletion: 'Confirmar exclusão',
    deleteThisAlbum: 'Excluir este album?',
    deleteWarning:
      'Todo o progresso de figurinhas e registros de investimentos deste album serão excluídos permanentemente. Esta ação não pode ser desfeita.',
    deletePermanently: 'Excluir permanentemente',

    albumSizeLabel: 'Tamanho',
    themeLabel: 'Tema',
    platformLabel: 'Plataforma',
    allAlbums: 'Seleção de albums',

    tabAlbum: 'Album',
    tabFlipbook: 'Folhear',
    tabHistory: 'Histórico',
    tabConfiguration: 'Configuração',

    collectedStickers: 'Figurinhas coletadas',
    investedAmount: 'Valor investido',
    targetFinishDate: 'Data alvo de conclusão',
    targetNotDefined: 'Não definida ainda',
    daysOverdue: (d) => `${d} dia${d !== 1 ? 's' : ''} atrasado`,
    daysRemaining: (d) => `${d} dia${d !== 1 ? 's' : ''} restante${d !== 1 ? 's' : ''}`,
    targetToday: 'Meta é hoje!',
    thisMonth: 'Este mês',
    activeMonth: 'Mês ativo',
    noActivityYet: 'Sem atividade ainda',
    investmentsThisMonth: (c) => `${c} investimento${c !== 1 ? 's' : ''}`,
    noInvestments: 'Sem investimentos',
    hideTarget: 'Ocultar meta',
    showTarget: 'Mostrar meta',
    ofTarget: 'da meta',

    stickerAlbum: 'Album de figurinhas',
    collectByInvesting: 'Colete investindo no destino selecionado',
    clickToRegister: 'Clique em qualquer figurinha para registrar o valor correspondente.',
    collected: 'Coletada',
    openSlot: 'Vaga livre',

    flipBook: 'Folhear Album',
    browsePageByPage: 'Navegue pela sua coleção página a página',
    clickToInvest: 'Clique em qualquer figurinha para investir ou ver detalhes.',
    previousPage: 'Página anterior',
    nextPage: 'Próxima página',
    pageOf: (c, t) => `Página ${c} / ${t}`,
    stickersRange: (f, t) => `figurinhas ${f}–${t}`,
    jumpToPage: 'Ir para página',

    investmentHistory: 'Histórico de investimentos',
    allRecordedInvestments: 'Todos os investimentos registrados neste album',
    exportCSV: 'Exportar CSV',
    noInvestmentsYet: 'Nenhum investimento registrado ainda.',
    noInvestmentsDesc: 'Colete sua primeira figurinha na aba Album.',
    investmentsCount: (c) => `${c} investimento${c !== 1 ? 's' : ''}`,
    total: 'Total',
    stickerCol: 'Nº',
    nameCol: 'Nome',
    valueCol: 'Valor',
    dateCol: 'Data',
    destinationCol: 'Destino',
    commentsCol: 'Observações',

    configurationTitle: 'Configuração',
    albumSettings: 'Configurações do album',
    settingsEditable: 'As configurações podem ser alteradas mesmo após o início',
    targetAmount: 'Valor alvo',
    revealCalculation: 'Revelar o cálculo completo do album',
    hideTotalTarget: 'Ocultar valor total',
    showTotalTarget: 'Mostrar valor total',
    sequenceFormula: 'Fórmula da sequência',
    investedSoFar: 'Investido até agora',
    targetHidden: 'O valor total fica oculto na tela do album e aparece apenas aqui.',
    savedInvestments: 'Investimentos salvos',
    preconfigureDestinations: 'Pré-configure os destinos usados no processo de figurinhas',
    noNotes: 'Sem observações.',
    remove: 'Remover',
    destinationName: 'Nome do destino',
    typeLabel: 'Tipo',
    notesLabel: 'Observações',
    addSavedInvestment: 'Adicionar investimento',
    currency: 'Moeda',
    targetFinishDateLabel: 'Data alvo de conclusão',

    noDeclaration: 'Não quero declarar',
    noDeclarationKind: 'Privado',
    noDeclarationNotes: 'Detalhes do investimento mantidos em sigilo.',

    stickerCollectedTitle: 'Figurinha coletada!',
    stickerCollectedClose: 'Fechar',

    rarityLabel: 'Raridade',

    stickerInvestment: 'Investimento da figurinha',
    resetSticker: 'Resetar figurinha',
    investIn: 'Investir em',
    comments: 'Observações',
    commentsPlaceholder: 'Por que esse investimento? O que você aprendeu?',
    invest: 'Investir',
    updateInvestment: 'Atualizar investimento',
    noDestinationsYet: 'Nenhum investimento salvo configurado ainda.',
    noDestinationsDesc: 'Adicione pelo menos um destino antes de coletar figurinhas.',
    goToConfiguration: 'Ir para Configuração',

    saveError: 'Falha ao salvar. Seu progresso pode não ter sido persistido.',
    saveRetry: 'Tentar novamente',

    authSubtitle: 'Entre para sincronizar seus albums entre dispositivos.',
    authEmail: 'E-mail',
    authPassword: 'Senha',
    authSignIn: 'Entrar',
    authSignUp: 'Criar conta',
    authMagicLink: 'Receber link por e-mail',
    authSendMagicLink: 'Enviar link mágico',
    authMagicLinkSent: 'Verifique seu e-mail para o link de acesso.',
    authConfirmEmail: 'Verifique seu e-mail para confirmar sua conta.',
    authSignOut: 'Sair',
    authForgotPassword: 'Esqueceu a senha?',
    authSendPasswordReset: 'Enviar e-mail de redefinição',
    authPasswordResetSent: 'Verifique seu e-mail para o link de redefinição de senha.',
    authUpdatePassword: 'Atualizar senha',
    authConfirmPassword: 'Confirmar senha',
    authPasswordMismatch: 'As senhas não coincidem.',
    authPasswordUpdated: 'Senha atualizada. Agora você pode entrar.',
    checkYourAlbums: 'Ver seus albums',

    marketingAriaLabel: 'Como o InvestAlbum funciona',
    marketingKicker: 'Construa seu patrimônio uma figurinha por vez',
    marketingTransformTitle: 'Transforme seus investimentos em uma coleção',
    marketingTransformLead:
      'Guardar dinheiro nem sempre é motivador. Os números crescem lentamente e, muitas vezes, fica difícil perceber o progresso.',
    marketingTransformP1:
      'No InvestAlbum, investir funciona como completar um album de figurinhas: cada album representa uma meta financeira e cada figurinha representa um valor de investimento.',
    marketingTransformP2:
      'Você pode colar as figurinhas na ordem que quiser. Não existe um caminho certo. O importante é manter consistência e seguir avançando.',
    marketingTransformP3:
      'Quando a última figurinha for colada, a soma de todos os investimentos registrados será exatamente o valor planejado para aquela meta.',
    marketingHowTitle: 'Como funciona',
    marketingHowIntro: 'Simples, visual e feito para criar o hábito de investir.',
    marketingStep1Title: '1. Escolha um album',
    marketingStep1Body:
      'Cada album possui uma quantidade fixa de figurinhas, como 30, 50 ou 100. Esse tamanho define a escala da sua jornada.',
    marketingStep2Title: '2. Entenda os valores das figurinhas',
    marketingStep2Body:
      'As figurinhas seguem uma sequência crescente de 1 até o tamanho do album. Números menores representam aportes menores; números maiores, aportes maiores.',
    marketingStep3Title: '3. Invista no seu ritmo',
    marketingStep3Body:
      'Sempre que fizer um investimento, escolha a figurinha correspondente ao valor e cole no album. Você pode começar pela 50, depois 3, depois 28, em qualquer ordem.',
    marketingStep4Title: '4. Complete o album',
    marketingStep4Body:
      'Ao preencher todas as figurinhas, o total investido alcança exatamente a meta. O InvestAlbum usa a soma de uma sequência aritmética para garantir esse resultado.',
    marketingGaussTitle: 'A história por trás da fórmula',
    marketingGaussBody:
      'A inspiração vem da história de Gauss: ao formar pares entre o início e o fim da sequência, a soma total pode ser calculada de forma rápida e exata.',
    marketingFormula: 'Soma = n * (n + 1) / 2',
    marketingHighlightsTitle: 'Por que isso funciona',
    marketingHighlight1:
      'Progresso visível: cada aporte vira uma conquista concreta no album, não apenas um número na tela.',
    marketingHighlight2:
      'Planejamento flexível: use destinos salvos, comentários e histórico sem perder autonomia no ritmo de investimento.',
    marketingHighlight3:
      'Continuidade segura: sincronização na web quando configurada e exportação/importação JSON para backup e migração.',
    marketingFooterLead: 'Invista. Aprenda. Colecione.',
    marketingFooterEmphasis: 'Conquiste.',
    marketingFooterTagline: 'Seu futuro merece ser incrível.',

    publicPagesLabel: 'Páginas públicas',
    publicHome: 'Início',
    publicHowTo: 'Como usar',
    publicAbout: 'Sobre nós',
    publicContact: 'Contato',
    publicTerms: 'Termos e condições',
    legalTermsTitle: 'Termos e condições',
    legalLoadError:
      'Não foi possível carregar o texto legal agora. Tente novamente em alguns instantes.',
    howToKicker: 'Primeiros passos',
    howToTitle: 'Como usar o InvestAlbum',
    howToLead:
      'O InvestAlbum é um site de registro gratuito para organizar sua jornada de investimentos com marcos visuais.',
    howToFreeRegistrationTitle: 'Registro gratuito',
    howToFreeRegistrationBody:
      'Criar conta é gratuito. Você pode entrar para sincronizar seus albums entre dispositivos e manter seu progresso com backup.',
    howToFlowTitle: 'Fluxo simples',
    howToFlow1Title: '1. Crie seu album',
    howToFlow1Body:
      'Escolha um tema que você goste. O tema selecionado define o tamanho do album e a jornada de figurinhas.',
    howToFlow2Title: '2. Escolha uma figurinha ao investir',
    howToFlow2Body:
      'Cada figurinha tem um número e um valor de investimento correspondente. Você pode colar em qualquer ordem.',
    howToFlow3Title: '3. Direcione para seu destino de investimento',
    howToFlow3Body:
      'Registre onde você investiu, como Tesouro, ETF ou CDB, para manter um histórico pessoal organizado.',
    howToFlow4Title: '4. Acompanhe o progresso até completar',
    howToFlow4Body:
      'Conforme as figurinhas são coletadas, o total investido avança até a meta do album com base na soma da sequência aritmética.',
    howToReminderTitle: 'Lembrete importante',
    howToReminderBody:
      'O InvestAlbum é apenas uma ferramenta de acompanhamento educacional. Não executa, não intermedeia e não operacionaliza transações financeiras.',
    aboutKicker: 'Sobre o InvestAlbum',
    aboutTitle: 'Nosso objetivo é incentivar a consistência nos investimentos',
    aboutLead:
      'O InvestAlbum existe para tornar a jornada de longo prazo mais visual, motivadora e fácil de acompanhar.',
    aboutMissionTitle: 'Nossa missão',
    aboutMissionP1:
      'Transformar metas financeiras abstratas em jornadas colecionáveis com progresso claro e recompensador.',
    aboutMissionP2:
      'A plataforma tem foco educacional e organizacional: cada figurinha é um marco, cada album é uma meta.',
    aboutNoIntermediationTitle: 'Sem intermediação financeira',
    aboutNoIntermediationP1:
      'O InvestAlbum não recebe, guarda, movimenta ou aplica seu dinheiro. Você investe diretamente na instituição de sua preferência e registra aqui apenas o acompanhamento.',
    aboutNoIntermediationP2:
      'Não há compra, venda, transferência, resgate ou administração de carteira dentro da plataforma.',
    aboutDisclaimer:
      'O InvestAlbum é uma ferramenta de educação e acompanhamento. Não oferece produtos financeiros, não presta consultoria e não realiza recomendação de investimentos.',
    contactKicker: 'Fale com a gente',
    contactTitle: 'Página de contato',
    contactLead:
      'Dúvidas, sugestões e relatos de problema são bem-vindos. Seu feedback ajuda a melhorar a experiência do produto.',
    contactPrimaryChannelTitle: 'Canal principal',
    contactPrimaryChannelBody:
      'Abra um chamado no repositório oficial com o máximo de contexto possível para facilitarmos a análise.',
    contactPrimaryCta: 'Abrir página de contato/chamados',
    contactWhatToSendTitle: 'O que enviar para agilizar',
    contactItem1: 'Descrição curta do ocorrido e do comportamento esperado.',
    contactItem2: 'Informações de navegador/dispositivo e idioma selecionado.',
    contactItem3: 'Passo a passo para reproduzir e capturas de tela, quando possível.',

    themeNames: {
      standard: 'Álbum Padrão',
      animals: '500 Animais',
      naturalWonders: '500 Maravilhas Naturais',
      architecture: 'Plantas de Arquitetura',
      artists: 'Artistas',
      backpacker: 'Atlas Mochileiro',
      botanical: 'Botânica',
      castle: 'Castelos e Fortalezas',
      compounds: 'Compostos',
      cosmos: 'Cosmos',
      inventions: 'Invenções',
      marine: 'Vida Marinha',
      mustVisitCities: '500 Cidades Imperdíveis no Mundo',
      mtgCommander: 'MTG Essenciais de Commander',
      mtgRevised: 'MTG Edição Revised',
      pokemon: 'Pokemon',
      pokemonOriginal151: 'Pokemon 151 Originais',
      rockroll: 'Hinos do Rock',
      soccer: 'Futebol',
      tourist: 'Maravilhas do Mundo',
      turningpoints: 'Pontos de Virada',
      unesco: 'UNESCO'
    },
    themeSoccer: '500 Lendas do Futebol',
    themeMtg: '500 Lendas do MTG',
    themeTourist: '500 Maravilhas do Mundo',
    themeRockroll: '500 Antemas do Rock',

    heroSoccer: 'Os maiores jogadores de futebol de todos os tempos — de Pelé até hoje.',
    heroMtg: 'As cartas de Magic: The Gathering mais poderosas e icônicas já impressas.',
    heroTourist: 'Da Torre Eiffel a Machu Picchu — os maiores destinos do mundo.',
    heroRockroll: 'As músicas e artistas que definiram a história do rock and roll.'
  }
}

export const DEFERRED_SPANISH_TRANSLATIONS: Translations = {
  appName: 'InvestAlbum',
  loading: 'Cargando tus álbumes',
  loadingDesc: 'Restaurando datos del álbum desde SQLite.',
  language: 'Idioma',
  settings: 'Configuración',
  usernameLabel: 'Usuario',
  guestUser: 'Usuario local',
  themeMode: 'Tema',
  darkMode: 'Activar modo oscuro',
  lightMode: 'Activar modo claro',
  quickLinks: 'Accesos rápidos',
  quickLinkHome: 'Inicio',
  quickLinkInvestments: 'Inversiones',
  quickLinkAlbumSelection: 'Selección de álbumes',
  quickLinkThemeReview: 'Revisión de temas',
  quickLinkHelp: 'Ayuda',
  albumPageSize: 'Tamaño de página del álbum',
  albumPageSizeValue: (size) => `${size} cromos por página`,
  helpTitle: 'Ayuda',
  helpDescription:
    'Usa las pestañas del álbum para coleccionar cromos, revisar inversiones y mantener destinos actualizados.',

  yourAlbums: 'Tus Álbumes',
  selectAlbumDesc: 'Selecciona un álbum para abrirlo o crea uno nuevo.',
  myInvestmentAlbum: 'Mi Álbum de Inversiones',
  newAlbum: 'Nuevo álbum',
  maxAlbumsReached: 'Se alcanzó el máximo de 50 álbumes.',
  openAlbum: 'Abrir álbum',
  deleteAlbum: 'Eliminar',

  newAlbumTitle: 'Nuevo álbum',
  configureCollection: 'Configura tu colección',
  albumName: 'Nombre del álbum',
  albumNamePlaceholder: 'Mi Viaje de Inversiones',
  albumSize: 'Tamaño del álbum',
  stickersLabel: 'cromos',
  theme: 'Tema',
  availableThemes: (count) => `${count} tamaños de álbum estándar disponibles`,
  createAlbum: 'Crear álbum',
  cancel: 'Cancelar',

  confirmDeletion: 'Confirmar eliminación',
  deleteThisAlbum: '¿Eliminar este álbum?',
  deleteWarning:
    'Todo el progreso de cromos y registros de inversiones de este álbum se eliminarán permanentemente. Esta acción no se puede deshacer.',
  deletePermanently: 'Eliminar permanentemente',

  albumSizeLabel: 'Tamaño',
  themeLabel: 'Tema',
  platformLabel: 'Plataforma',
  allAlbums: 'Selección de álbumes',

  tabAlbum: 'Álbum',
  tabFlipbook: 'Hojear',
  tabHistory: 'Historial',
  tabConfiguration: 'Configuración',

  collectedStickers: 'Cromos recolectados',
  investedAmount: 'Monto invertido',
  targetFinishDate: 'Fecha objetivo',
  targetNotDefined: 'No definida aún',
  daysOverdue: (d) => `${d} día${d !== 1 ? 's' : ''} de retraso`,
  daysRemaining: (d) => `${d} día${d !== 1 ? 's' : ''} restante${d !== 1 ? 's' : ''}`,
  targetToday: '¡El objetivo es hoy!',
  thisMonth: 'Este mes',
  activeMonth: 'Mes activo',
  noActivityYet: 'Sin actividad aún',
  investmentsThisMonth: (c) => `${c} inversión${c !== 1 ? 'es' : ''}`,
  noInvestments: 'Sin inversiones',
  hideTarget: 'Ocultar meta',
  showTarget: 'Mostrar meta',
  ofTarget: 'de la meta',

  stickerAlbum: 'Álbum de cromos',
  collectByInvesting: 'Colecciona invirtiendo en el destino seleccionado',
  clickToRegister: 'Haz clic en cualquier cromo para registrar el monto correspondiente.',
  collected: 'Recolectado',
  openSlot: 'Espacio libre',

  flipBook: 'Hojear Álbum',
  browsePageByPage: 'Navega por tu colección página a página',
  clickToInvest: 'Haz clic en cualquier cromo para invertir o ver detalles.',
  previousPage: 'Página anterior',
  nextPage: 'Página siguiente',
  pageOf: (c, t) => `Página ${c} / ${t}`,
  stickersRange: (f, t) => `cromos ${f}–${t}`,
  jumpToPage: 'Ir a la página',

  investmentHistory: 'Historial de inversiones',
  allRecordedInvestments: 'Todas las inversiones registradas en este álbum',
  exportCSV: 'Exportar CSV',
  noInvestmentsYet: 'Aún no hay inversiones registradas.',
  noInvestmentsDesc: 'Recoge tu primer cromo desde la pestaña Álbum.',
  investmentsCount: (c) => `${c} inversión${c !== 1 ? 'es' : ''}`,
  total: 'Total',
  stickerCol: 'Nº',
  nameCol: 'Nombre',
  valueCol: 'Valor',
  dateCol: 'Fecha',
  destinationCol: 'Destino',
  commentsCol: 'Comentarios',

  configurationTitle: 'Configuración',
  albumSettings: 'Ajustes del álbum',
  settingsEditable: 'Los ajustes pueden modificarse incluso después de comenzar',
  targetAmount: 'Monto objetivo',
  revealCalculation: 'Revelar el cálculo completo del álbum',
  hideTotalTarget: 'Ocultar total objetivo',
  showTotalTarget: 'Mostrar total objetivo',
  sequenceFormula: 'Fórmula de secuencia',
  investedSoFar: 'Invertido hasta ahora',
  targetHidden: 'El total objetivo está oculto en la pantalla del álbum y solo aparece aquí.',
  savedInvestments: 'Inversiones guardadas',
  preconfigureDestinations: 'Preconfigura los destinos usados en el flujo de cromos',
  noNotes: 'Sin notas.',
  remove: 'Eliminar',
  destinationName: 'Nombre del destino',
  typeLabel: 'Tipo',
  notesLabel: 'Notas',
  addSavedInvestment: 'Agregar inversión',
  currency: 'Moneda',
  targetFinishDateLabel: 'Fecha objetivo',

  noDeclaration: 'No quiero declarar',
  noDeclarationKind: 'Privado',
  noDeclarationNotes: 'Detalles de la inversión mantenidos en privado.',

  stickerCollectedTitle: '¡Cromo recolectado!',
  stickerCollectedClose: 'Cerrar',

  rarityLabel: 'Rareza',

  stickerInvestment: 'Inversión del cromo',
  resetSticker: 'Restablecer cromo',
  investIn: 'Invertir en',
  comments: 'Comentarios',
  commentsPlaceholder: '¿Por qué esta inversión? ¿Qué aprendiste?',
  invest: 'Invertir',
  updateInvestment: 'Actualizar inversión',
  noDestinationsYet: 'No hay inversiones guardadas configuradas aún.',
  noDestinationsDesc: 'Agrega al menos un destino antes de recolectar cromos.',
  goToConfiguration: 'Ir a Configuración',

  saveError: 'Error al guardar. Es posible que tu progreso no se haya guardado.',
  saveRetry: 'Reintentar',

  authSubtitle: 'Inicia sesión para sincronizar tus álbumes entre dispositivos.',
  authEmail: 'Correo electrónico',
  authPassword: 'Contraseña',
  authSignIn: 'Iniciar sesión',
  authSignUp: 'Crear cuenta',
  authMagicLink: 'Envíame un enlace',
  authSendMagicLink: 'Enviar enlace mágico',
  authMagicLinkSent: 'Revisa tu correo para el enlace de acceso.',
  authConfirmEmail: 'Revisa tu correo para confirmar tu cuenta.',
  authSignOut: 'Cerrar sesión',
  authForgotPassword: '¿Olvidaste tu contraseña?',
  authSendPasswordReset: 'Enviar correo de restablecimiento',
  authPasswordResetSent: 'Revisa tu correo para el enlace de restablecimiento de contraseña.',
  authUpdatePassword: 'Actualizar contraseña',
  authConfirmPassword: 'Confirmar contraseña',
  authPasswordMismatch: 'Las contraseñas no coinciden.',
  authPasswordUpdated: 'Contraseña actualizada. Ahora puedes iniciar sesión.',
  checkYourAlbums: 'Revisa tus álbumes',

  marketingAriaLabel: 'Cómo funciona InvestAlbum',
  marketingKicker: 'Construye tu patrimonio, cromo a cromo',
  marketingTransformTitle: 'Convierte tus inversiones en una colección',
  marketingTransformLead:
    'Ahorrar dinero no siempre es motivador. Los números crecen lentamente y muchas veces es difícil percibir el progreso.',
  marketingTransformP1:
    'En InvestAlbum, invertir funciona como completar un álbum de cromos: cada álbum representa una meta financiera y cada cromo representa un monto de inversión.',
  marketingTransformP2:
    'Puedes pegar los cromos en el orden que quieras. No hay un camino único. Lo importante es mantener la constancia y seguir avanzando.',
  marketingTransformP3:
    'Cuando pegues el último cromo, la suma de todas las inversiones registradas será exactamente el valor planificado para esa meta.',
  marketingHowTitle: 'Cómo funciona',
  marketingHowIntro: 'Simple, visual y diseñado para crear el hábito de invertir.',
  marketingStep1Title: '1. Elige un álbum',
  marketingStep1Body:
    'Cada álbum tiene una cantidad fija de cromos, como 30, 50 o 100. Ese tamaño define la escala de tu recorrido.',
  marketingStep2Title: '2. Comprende el valor de cada cromo',
  marketingStep2Body:
    'Los cromos siguen una secuencia creciente desde 1 hasta el tamaño del álbum. Números bajos representan aportes menores; números altos, aportes mayores.',
  marketingStep3Title: '3. Invierte a tu ritmo',
  marketingStep3Body:
    'Cada vez que inviertas, elige el cromo que corresponda a ese monto y colecciónalo. Puedes empezar por el 50, luego el 3, luego el 28, en cualquier orden.',
  marketingStep4Title: '4. Completa la colección',
  marketingStep4Body:
    'Al completar todos los cromos, el total invertido alcanza exactamente la meta. InvestAlbum usa la suma de una secuencia aritmética para garantizar ese resultado.',
  marketingGaussTitle: 'La historia detrás de la fórmula',
  marketingGaussBody:
    'La inspiración viene de la historia clásica de Gauss: al formar pares entre los extremos de la secuencia, el total puede calcularse de forma rápida y exacta.',
  marketingFormula: 'Suma = n * (n + 1) / 2',
  marketingHighlightsTitle: 'Por qué ayuda',
  marketingHighlight1:
    'Impulso visual: cada aporte se convierte en un avance concreto dentro del álbum, no solo en un número.',
  marketingHighlight2:
    'Planificación flexible: usa destinos guardados, comentarios e historial sin perder libertad en tu ritmo de inversión.',
  marketingHighlight3:
    'Continuidad segura: sincronización web cuando está configurada y exportación/importación JSON para respaldo y migración.',
  marketingFooterLead: 'Invierte. Aprende. Colecciona.',
  marketingFooterEmphasis: 'Conquista.',
  marketingFooterTagline: 'Tu futuro merece ser increíble.',

  publicPagesLabel: 'Páginas públicas',
  publicHome: 'Inicio',
  publicHowTo: 'Como usar',
  publicAbout: 'Sobre nosotros',
  publicContact: 'Contacto',
  publicTerms: 'Términos y condiciones',
  legalTermsTitle: 'Términos y condiciones',
  legalLoadError: 'No se pudo cargar el texto legal ahora. Inténtalo de nuevo en unos momentos.',
  howToKicker: 'Primeros pasos',
  howToTitle: 'Como usar InvestAlbum',
  howToLead:
    'InvestAlbum es un sitio de registro gratuito para organizar tu camino de inversión con progreso visual.',
  howToFreeRegistrationTitle: 'Registro gratuito',
  howToFreeRegistrationBody:
    'Crear una cuenta es gratis. Puedes iniciar sesión para sincronizar tus álbumes entre dispositivos y conservar tu progreso.',
  howToFlowTitle: 'Flujo simple',
  howToFlow1Title: '1. Crea tu álbum',
  howToFlow1Body:
    'Elige un tema que te guste. El tema seleccionado define el tamaño del álbum y la ruta de cromos.',
  howToFlow2Title: '2. Elige un cromo al invertir',
  howToFlow2Body:
    'Cada cromo tiene un número y un monto de inversión correspondiente. Puedes pegar cromos en cualquier orden.',
  howToFlow3Title: '3. Vincula el destino de la inversión',
  howToFlow3Body:
    'Registra dónde invertiste, como bono del tesoro, ETF o depósito, para mantener un historial personal claro.',
  howToFlow4Title: '4. Sigue el progreso hasta completar',
  howToFlow4Body:
    'A medida que coleccionas cromos, el total invertido avanza hasta la meta del álbum basada en la suma de la secuencia aritmética.',
  howToReminderTitle: 'Recordatorio importante',
  howToReminderBody:
    'InvestAlbum es solo una herramienta educativa de seguimiento. No ejecuta ni intermedia transacciones financieras.',
  aboutKicker: 'Sobre InvestAlbum',
  aboutTitle: 'Nuestra intención es estimular la constancia al invertir',
  aboutLead:
    'InvestAlbum existe para que la jornada de inversión a largo plazo sea más visual, motivadora y fácil de seguir.',
  aboutMissionTitle: 'Nuestra misión',
  aboutMissionP1:
    'Convertir metas financieras abstractas en recorridos coleccionables con progreso claro y celebrable.',
  aboutMissionP2:
    'La plataforma tiene un propósito educativo y organizativo: cada cromo es un hito y cada álbum es una meta.',
  aboutNoIntermediationTitle: 'Sin intermediación financiera',
  aboutNoIntermediationP1:
    'InvestAlbum no recibe, guarda, mueve ni invierte tu dinero. Inviertes directamente con tu institución financiera y aquí solo registras el avance.',
  aboutNoIntermediationP2:
    'No hay operaciones de compra, venta, transferencias, rescates ni gestión de cartera dentro de la plataforma.',
  aboutDisclaimer:
    'InvestAlbum es una herramienta de educación y seguimiento. No ofrece productos financieros, no brinda asesoría y no hace recomendaciones de inversión.',
  contactKicker: 'Habla con nosotros',
  contactTitle: 'Página de contacto',
  contactLead:
    'Preguntas, sugerencias y reportes de errores son bienvenidos. Tu feedback nos ayuda a mejorar la experiencia.',
  contactPrimaryChannelTitle: 'Canal principal',
  contactPrimaryChannelBody:
    'Abre un issue en el repositorio oficial con el mayor contexto posible para que podamos analizarlo rápido.',
  contactPrimaryCta: 'Abrir página de contacto/issues',
  contactWhatToSendTitle: 'Qué enviar para agilizar la respuesta',
  contactItem1: 'Descripción breve de lo ocurrido y de lo esperado.',
  contactItem2: 'Información del dispositivo/navegador e idioma seleccionado.',
  contactItem3: 'Pasos para reproducir y capturas de pantalla cuando sea posible.',

  themeNames: {
    standard: 'Álbum Estándar',
    animals: '500 Animales',
    naturalWonders: '500 Maravillas Naturales',
    architecture: 'Planos de Arquitectura',
    artists: 'Artistas',
    backpacker: 'Atlas Mochilero',
    botanical: 'Botánica',
    castle: 'Castillos y Fortalezas',
    compounds: 'Compuestos',
    cosmos: 'Cosmos',
    inventions: 'Invenciones',
    marine: 'Vida Marina',
    mustVisitCities: '500 Ciudades Imperdibles del Mundo',
    mtgCommander: 'MTG Básicos de Commander',
    mtgRevised: 'MTG Edición Revised',
    pokemon: 'Pokemon',
    pokemonOriginal151: 'Pokemon 151 Originales',
    rockroll: 'Himnos del Rock',
    soccer: 'Fútbol',
    tourist: 'Maravillas del Mundo',
    turningpoints: 'Puntos de Inflexión',
    unesco: 'UNESCO'
  },
  themeSoccer: '500 Leyendas del Fútbol',
  themeMtg: '500 Leyendas de MTG',
  themeTourist: '500 Maravillas del Mundo',
  themeRockroll: '500 Himnos del Rock',

  heroSoccer: 'Los mejores futbolistas de todos los tiempos — desde Pelé hasta hoy.',
  heroMtg: 'Las cartas de Magic: The Gathering más poderosas e icónicas jamás impresas.',
  heroTourist: 'Desde la Torre Eiffel hasta Machu Picchu — los mejores destinos del mundo.',
  heroRockroll: 'Las canciones y artistas que definieron la historia del rock and roll.'
}

export const TRANSLATIONS = ACTIVE_TRANSLATIONS

export const LOCALE_LABELS: Record<Locale, string> = {
  en: 'English',
  'pt-BR': 'Português (BR)'
}

export const LOCALE_FLAGS: Record<Locale, string> = {
  en: '🇺🇸',
  'pt-BR': '🇧🇷'
}
